import {useCallback} from 'react';

import Store, {CHANGE_EVENT} from 'lib/Store';
import useEventHandler from 'ui-library/hooks/useEventHandler';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

/**
 * Hook analogous to useSelector to extract data from a single Flux Store.
 *
 * The selector function should be cached/memoized (e.g. with useCallback) to
 * avoid thrashing of repeatedly registering and unregistering it as an event handler.
 *
 * It is currently pretty naive about emitting updates. For every change to the store
 * it will evaluate the new state and unless the new state is strictly equal to the previous,
 * updating this state will trigger a rerender.
 *
 * If you are returning an object, you should either memoize the selector or provide an equalityFn to avoid
 * unnecessary re-renders. Ideally, use a deep equality function like isEqual from react-fast-compare, just
 * make sure you're not comparing giant objects as this will be expensive.
 *
 * Example usage:
 * function Component() {
 *   const workspaceItems = useStoreSelector(
 *     WorkspaceStore,
 *     useCallback((workspaceStore) => workspaceStore.workspaceItems, [])
 *   );
 * }
 */
export default function useStoreSelector<S extends Store, T>(
  store: S,
  selector: (store: S) => T,
  equalityFn?: (a: T, b: T) => boolean
): T {
  // Follow the pattern of connectComponentToStore by ensuring that we only emit state
  // changes while the component is mounted.
  const [state, setState] = useStateIfMounted(() => selector(store));
  const updateCallback = useCallback(() => {
    if (!equalityFn) {
      setState(selector(store));
    } else {
      const newState = selector(store);
      if (!equalityFn(newState, state)) {
        setState(newState);
      }
    }
  }, [store, state, setState, selector, equalityFn]);
  useEventHandler(store, CHANGE_EVENT, updateCallback);
  return state;
}
